import { gainAt, type GainPoint, type MixPlan } from './edit-plan.js';

/**
 * The mix of an edit on a Web Audio graph: every voice of the plan (a piece's own sound at its speed, a take, a bed)
 * as a buffer source with its level automated, into its track (video, voice-over, the pieces' separated sound, music: the music through its
 * envelope of drops), into the master (the end fade), into `out`. The monitor schedules from the playhead on a live
 * context; the export schedules from zero on an offline one; both from the same plan.
 */
export interface MixGraph {
  /** Silence everything this schedule started. */
  stop(): void;
}

/** A level's points from `fromS` on, as automation starting at `startAt` of the context's clock. */
function automate(param: AudioParam, points: GainPoint[], fromS: number, startAt: number): void {
  param.cancelScheduledValues(0);
  param.setValueAtTime(gainAt(points, fromS), startAt);
  for (const pt of points) if (pt.atS > fromS) param.linearRampToValueAtTime(pt.gain, startAt + (pt.atS - fromS));
}

export function scheduleMix(ctx: BaseAudioContext, plan: MixPlan, bufferOf: (key: string) => AudioBuffer | null, fromS: number, startAt: number, out: AudioNode): MixGraph {
  const sources: AudioBufferSourceNode[] = [];
  const nodes: AudioNode[] = [];
  const gain = (value: number) => {
    const g = ctx.createGain();
    g.gain.value = value;
    nodes.push(g);
    return g;
  };
  const master = gain(1);
  automate(master.gain, plan.master, fromS, startAt);
  master.connect(out);
  const tracks = { video: gain(plan.tracks.video), vo: gain(plan.tracks.vo), music: gain(plan.tracks.music), sound: gain(plan.tracks.sound) };
  const musicEnv = gain(1);
  automate(musicEnv.gain, plan.musicEnvelope, fromS, startAt);
  tracks.video.connect(master);
  tracks.vo.connect(master);
  tracks.sound.connect(master);
  tracks.music.connect(musicEnv).connect(master);
  for (const v of plan.voices) {
    const buffer = bufferOf(v.key);
    if (!buffer) continue;
    const end = v.atS + v.durS;
    if (end <= fromS) continue;
    const skip = Math.max(0, fromS - v.atS);
    const offset = v.offsetS + skip * v.rate;
    const dur = v.durS - skip;
    if (offset >= buffer.duration || dur <= 0.001) continue;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.playbackRate.value = v.rate;
    const g = gain(1);
    automate(g.gain, v.points, Math.max(fromS, v.atS), startAt + Math.max(0, v.atS - fromS));
    src.connect(g).connect(tracks[v.track]);
    const when = startAt + Math.max(0, v.atS - fromS);
    // stopped on the context's clock: start()'s own duration counts the file's seconds, which a speed changes
    src.start(when, offset);
    src.stop(when + dur);
    sources.push(src);
  }
  return {
    stop() {
      for (const s of sources) {
        try {
          s.stop();
        } catch {
          /* not started yet or already ended */
        }
        s.disconnect();
      }
      for (const n of nodes) n.disconnect();
    },
  };
}
